import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, UserPlus, Trash2, Trophy, Search } from "lucide-react";
import Layout from "../../components/Layout";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function AdminChallengeQualifications() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [items, setItems] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ch, qs] = await Promise.all([
        api.get(`/challenges/${id}`),
        api.get(`/admin/challenges/${id}/qualifications`),
      ]);
      setChallenge(ch.data);
      setItems(qs.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const addOne = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Renseigne un email");
    setAdding(true);
    try {
      await api.post(`/admin/challenges/${id}/qualifications`, {
        email: email.trim(),
        admin_note: note.trim() || null,
      });
      toast.success(`${email} est qualifié`);
      setEmail("");
      setNote("");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const removeOne = async (qualId, nickname) => {
    if (!window.confirm(`Retirer la qualification de ${nickname} ?`)) return;
    setRemovingId(qualId);
    try {
      await api.delete(`/admin/qualifications/${qualId}`);
      toast.success("Qualification retirée");
      await fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRemovingId(null);
    }
  };

  const isQualifiedOnly = challenge?.access_mode === "qualified_only";
  const active = (items || []).filter((q) => q.status !== "cancelled");
  const cancelled = (items || []).filter((q) => q.status === "cancelled");

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <button
          data-testid="admin-quals-back"
          onClick={() => navigate(`/admin/challenges/${id}`)}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l&apos;administration du défi
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.3em] font-mono-stat mb-2">
            <Trophy className="w-3.5 h-3.5" />
            Qualifications de la finale
          </div>
          <h1
            data-testid="admin-quals-title"
            className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[1]"
          >
            {challenge?.title || "…"}
          </h1>
          <div className="text-white/50 text-sm mt-2">
            {isQualifiedOnly
              ? "Ce défi est réservé aux joueurs qualifiés."
              : "Ce défi n'est pas en accès qualified_only — ajout manuel désactivé."}
          </div>
        </div>

        {isQualifiedOnly && (
          <form
            data-testid="admin-quals-add-form"
            onSubmit={addOne}
            className="panel rounded-2xl p-5 sm:p-6 mb-8 space-y-4 border border-cyan-400/30"
          >
            <div className="flex items-center gap-2 text-cyan-300 uppercase tracking-widest text-xs font-mono-stat">
              <UserPlus className="w-4 h-4" />
              Ajouter un joueur qualifié manuellement
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat mb-1">
                  Email du joueur *
                </div>
                <div className="flex items-center gap-2 bg-black/40 rounded-xl px-3 py-2 border border-white/10 focus-within:border-cyan-400/50">
                  <Search className="w-4 h-4 text-white/40 shrink-0" />
                  <input
                    data-testid="admin-quals-add-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="joueur@example.com"
                    type="email"
                    required
                    className="bg-transparent outline-none w-full text-white"
                  />
                </div>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-stat mb-1">
                  Note (raison, contexte)
                </div>
                <input
                  data-testid="admin-quals-add-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={400}
                  placeholder="Ex : Geste commercial, repêchage..."
                  className="w-full bg-black/40 rounded-xl px-3 py-2 border border-white/10 focus:border-cyan-400/50 outline-none text-white"
                />
              </label>
            </div>
            <button
              data-testid="admin-quals-add-submit"
              type="submit"
              disabled={adding}
              className="px-6 py-3 rounded-xl bg-cyan-400 text-black font-display font-bold uppercase tracking-wider hover:bg-cyan-300 active:scale-95 disabled:opacity-50 transition-all"
            >
              {adding ? "Ajout en cours…" : "Qualifier ce joueur"}
            </button>
          </form>
        )}

        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight">
            Joueurs qualifiés
          </h2>
          <span
            data-testid="admin-quals-count"
            className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-mono-stat"
          >
            {active.length} qualifié{active.length > 1 ? "s" : ""}
          </span>
        </div>

        {items === null ? (
          <div className="panel rounded-2xl p-6 text-white/60 text-sm">
            Chargement…
          </div>
        ) : active.length === 0 ? (
          <div
            data-testid="admin-quals-empty"
            className="panel rounded-2xl p-8 text-center text-white/50 text-sm"
          >
            Personne n&apos;est encore qualifié pour cette finale.
          </div>
        ) : (
          <div className="panel rounded-2xl overflow-hidden border border-white/10">
            <table className="w-full text-sm" data-testid="admin-quals-table">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50 font-mono-stat">
                <tr>
                  <th className="px-4 py-3 text-left">Joueur</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Origine</th>
                  <th className="px-4 py-3 text-right">Rang</th>
                  <th className="px-4 py-3 text-right">Score</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {active.map((q) => (
                  <tr
                    key={q.id}
                    data-testid={`admin-qual-row-${q.id}`}
                    className="border-t border-white/5"
                  >
                    <td className="px-4 py-3 font-display font-bold">
                      {q.nickname || "—"}
                    </td>
                    <td className="px-4 py-3 font-mono-stat text-xs text-white/60">
                      {q.email || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-stat ${
                          q.source === "manual_admin"
                            ? "bg-amber-400/15 text-amber-200 border border-amber-400/40"
                            : "bg-cyan-400/15 text-cyan-200 border border-cyan-400/40"
                        }`}
                      >
                        {q.source === "manual_admin" ? "Manuel" : "Auto"}
                      </span>
                      {q.source === "manual_admin" && q.admin_note && (
                        <div className="text-[10px] text-white/40 italic mt-1 max-w-xs">
                          « {q.admin_note} »
                        </div>
                      )}
                      {q.source !== "manual_admin" && q.source_challenge_title && (
                        <div className="text-[10px] text-white/40 mt-1 max-w-xs truncate">
                          {q.source_challenge_title}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-stat">
                      {q.source_rank ? `#${q.source_rank}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-stat">
                      {q.source_score || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-mono-stat ${
                          q.status === "used"
                            ? "bg-white/10 text-white/60"
                            : "bg-emerald-400/15 text-emerald-200 border border-emerald-400/40"
                        }`}
                      >
                        {q.status === "used" ? "Utilisée" : "Qualifié"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-white/60 font-mono-stat">
                      {fmtDate(q.qualified_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`admin-qual-remove-${q.id}`}
                        onClick={() => removeOne(q.id, q.nickname)}
                        disabled={removingId === q.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-pink-500/20 hover:text-pink-300 text-xs disabled:opacity-50 transition-all"
                        title="Retirer la qualification"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cancelled.length > 0 && (
          <details className="mt-8" data-testid="admin-quals-cancelled">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-white/40 font-mono-stat">
              Historique — {cancelled.length} qualification{cancelled.length > 1 ? "s" : ""} annulée{cancelled.length > 1 ? "s" : ""}
            </summary>
            <ul className="mt-3 space-y-1 text-xs text-white/50">
              {cancelled.map((q) => (
                <li key={q.id}>
                  {q.nickname} · {q.email || "—"} · annulée le {fmtDate(q.qualified_at)}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Layout>
  );
}
